import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import EventIcon from "@mui/icons-material/Event";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { withTranslation } from "react-i18next";
import QueueHeaderTooltip from "./QueueHeaderTooltip";
import { normalizeLane } from "./queueModel";

export var LANE_META = [
  {
    id: "new_orders",
    titleKey: "Queue.Lane.NewOrders",
    subtitleKey: "Queue.Lane.NewOrdersSubtitle",
    title: "New Orders",
    subtitle: "Counter, Courier, Curbside, Bedside",
    helpKey: "Queue.LaneHelp.NewOrders",
    help:
      "Newly created delivery orders for Counter, Courier, Curbside, Bedside, and Kiosk. Refills are not New Orders.",
    icon: DescriptionIcon,
    accent: "#2563eb",
  },
  {
    id: "delivery_change",
    titleKey: "Queue.Lane.DeliveryChange",
    subtitleKey: "Queue.Lane.DeliveryChangeSubtitle",
    title: "Delivery Changes",
    subtitle: "Kiosk, Counter, Curbside, Bedside, Courier",
    helpKey: "Queue.LaneHelp.DeliveryChange",
    help:
      "A patient asked to change pickup or delivery method. This includes Kiosk, Counter, Curbside, Bedside, Courier, and mail order switches.",
    icon: SwapHorizIcon,
    accent: "#0f766e",
  },
  {
    id: "due_today",
    titleKey: "Queue.Lane.DueToday",
    subtitleKey: "Queue.Lane.DueTodaySubtitle",
    title: "Due Today",
    subtitle: "Due or overdue",
    helpKey: "Queue.LaneHelp.DueToday",
    help: "Open tasks whose due date is today or already past.",
    icon: EventIcon,
    accent: "#c2410c",
  },
  {
    id: "exceptions",
    titleKey: "Queue.Lane.Exceptions",
    subtitleKey: "Queue.Lane.ExceptionsSubtitle",
    title: "Exceptions",
    subtitle: "Need attention",
    helpKey: "Queue.LaneHelp.Exceptions",
    help:
      "Failed or blocked pickup and delivery problems that need staff action.",
    icon: ReportProblemIcon,
    accent: "#dc2626",
  },
  {
    id: "completed",
    titleKey: "Queue.Lane.Completed",
    subtitleKey: "Queue.Lane.CompletedSubtitle",
    title: "Completed Today",
    subtitle: "Finished",
    helpKey: "Queue.LaneHelp.Completed",
    help: "Tasks marked Complete today. Open, In Progress, and Cancelled stay in the other lanes.",
    icon: CheckCircleIcon,
    accent: "#64748b",
  },
];

function translateLane(t, key, fallback) {
  if (!t) return fallback || "";
  return t(key, { defaultValue: fallback || "" });
}

export function laneTitle(lane, t) {
  if (!lane) return "";
  return translateLane(t, lane.titleKey, lane.title);
}

export function laneSubtitle(lane, t) {
  if (!lane) return "";
  return translateLane(t, lane.subtitleKey, lane.subtitle);
}

export function laneHelpContent(lane, t) {
  if (!lane) return "";
  var translate = t || function (_key, options) {
    return (options && options.defaultValue) || "";
  };
  return (
    <span>
      <span className="queue-header-tooltip-title">{laneTitle(lane, translate)}</span>
      <span className="queue-header-tooltip-body">
        {translate(lane.helpKey, { defaultValue: lane.help })}
      </span>
    </span>
  );
}

export function laneCount(summary, laneId) {
  if (!summary) return 0;
  var normalized = normalizeLane(laneId);
  if (!normalized || normalized === "all") return summary.total || 0;
  return summary[normalized] || 0;
}

export function laneById(laneId) {
  var normalized = normalizeLane(laneId);
  var i;
  for (i = 0; i < LANE_META.length; i += 1) {
    if (LANE_META[i].id === normalized) return LANE_META[i];
  }
  return null;
}

export function QueueCountBadge(props) {
  var loading = props.loading;
  var count = props.count || 0;
  var highlight = props.highlight;
  var size = props.size || 20;
  if (loading) {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
        data-testid="queue-count-loading"
      >
        <CircularProgress
          size={Math.max(12, size - 4)}
          thickness={5}
          sx={{ color: highlight ? "#fff" : "#64748b" }}
        />
      </Box>
    );
  }
  var displayCount = count > 99 ? "99+" : String(count);
  return (
    <Box
      sx={{
        minWidth: size,
        height: size,
        px: 0.5,
        borderRadius: size / 2 + "px",
        backgroundColor: count ? "#ef4444" : "#cbd5e1",
        color: count ? "#fff" : "#475569",
        fontSize: 11,
        fontWeight: 700,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        lineHeight: 1,
        flexShrink: 0,
      }}
    >
      {displayCount}
    </Box>
  );
}

function QueueLaneTilesView(props) {
  var summary = props.summary || {};
  var activeLane = normalizeLane(props.activeLane);
  var onSelect = props.onSelect;
  var loading = Boolean(props.loading);
  var t = props.t;

  return (
    <Box
      data-testid="queue-lane-tiles"
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
        gap: 1,
      }}
    >
      {LANE_META.map(function (lane) {
        var count = laneCount(summary, lane.id);
        var selected = activeLane === lane.id;
        var highlight = selected;
        var Icon = lane.icon;
        return (
          <QueueHeaderTooltip
            key={lane.id}
            wide
            block
            title={laneHelpContent(lane, t)}
          >
            <Box
              role="button"
              tabIndex={0}
              data-testid={"queue-lane-" + lane.id}
              onClick={function () {
                if (onSelect) onSelect(lane.id);
              }}
              onKeyDown={function (event) {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  if (onSelect) onSelect(lane.id);
                }
              }}
              sx={{
                cursor: "pointer",
                borderRadius: "10px",
                px: 1.25,
                py: 1,
                minHeight: 56,
                display: "flex",
                alignItems: "center",
                gap: 1,
                backgroundColor: highlight ? "#2563eb" : "#fff",
                color: highlight ? "#fff" : "#0f172a",
                border: highlight ? "2px solid #2563eb" : "1px solid #e2e8f0",
                "&:hover": { borderColor: highlight ? "#2563eb" : "#94a3b8" },
                boxSizing: "border-box",
              }}
            >
              <Icon
                sx={{
                  fontSize: 22,
                  color: highlight ? "#fff" : lane.accent,
                  flexShrink: 0,
                }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.4,
                    minWidth: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: 13,
                      lineHeight: 1.2,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {laneTitle(lane, t)}
                  </Typography>
                  <HelpOutlineIcon
                    sx={{
                      fontSize: 14,
                      opacity: highlight ? 0.85 : 0.45,
                      flexShrink: 0,
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    opacity: highlight ? 0.85 : 0.6,
                    lineHeight: 1.2,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {laneSubtitle(lane, t)}
                </Typography>
              </Box>
              <QueueCountBadge
                loading={loading}
                count={count}
                highlight={highlight}
                size={20}
              />
            </Box>
          </QueueHeaderTooltip>
        );
      })}
    </Box>
  );
}

export var QueueLaneTiles = withTranslation()(QueueLaneTilesView);
export default QueueLaneTiles;



// WEBPACK FOOTER //
// ./src/components/workflowQueue/QueueLaneTiles.js